'use client';
import type { ReactElement, ReactNode } from 'react';
import { IconChevronLeft } from '@tabler/icons-react';
import { BUTTON_ICON_POSITION, Cbutton } from '@/components/ui/Cbutton';
import { Canimatedswitch } from '@/components/ui/Canimatedswitch';
import { Ccontactformstep } from '@/components/ui/Ccontactformstep';
import { Ccontainer } from '@/components/ui/Ccontainer';
import { Chackerheader } from '@/components/ui/Chackerheader';
import { ButtonPreset } from '@/constants/componentPresets';
import { Temper } from '@/types/frontend-types';
import { CONTACT_PROCEED_BUTTON_LABEL, CONTACT_SUBMIT_BUTTON_LABEL, ContactFormStep, isValidEmail, } from './footerModels';
export interface CfooterContactFormProps {
    contactStep: ContactFormStep;
    name: string;
    email: string;
    message: string;
    nameError: string | null;
    emailError: string | null;
    messageError: string | null;
    isSubmitting: boolean;
    onStartContactForm: () => void;
    onNameChange: (value: string) => void;
    onEmailChange: (value: string) => void;
    onMessageChange: (value: string) => void;
    onNameProceed: () => void;
    onEmailProceed: () => void;
    onSubmit: () => void;
    onBackStep: () => void;
    onErrorBackToName: () => void;
}
type ContactStepRenderer = (props: CfooterContactFormProps) => ReactNode;
const CONTACT_STEP_RENDERERS: Record<ContactFormStep, ContactStepRenderer> = {
    [ContactFormStep.BeforeStep]: renderBeforeStep,
    [ContactFormStep.NameStep]: renderNameStep,
    [ContactFormStep.EmailStep]: renderEmailStep,
    [ContactFormStep.MessageStep]: renderMessageStep,
    [ContactFormStep.DoneStep]: renderDoneStep,
    [ContactFormStep.ErrorStep]: renderErrorStep,
};
function renderBeforeStep({ onStartContactForm }: CfooterContactFormProps): ReactNode {
    return (<Ccontainer display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      <Cbutton preset={ButtonPreset.Primary} label="Contact The CABAL" onClick={onStartContactForm} backgroundOpacity={0.1} borderOpacity={0} padding="20px 50px" textSize={20}/>
    </Ccontainer>);
}
function renderNameStep({ name, nameError, onNameChange, onNameProceed, onBackStep, }: CfooterContactFormProps): ReactNode {
    return (<Ccontactformstep placeholder={nameError ?? 'Who Speaks?'} value={name} onChange={onNameChange} buttonText={CONTACT_PROCEED_BUTTON_LABEL} onProceed={onNameProceed} onBack={onBackStep} isError={!!nameError} isDisabled={name.trim().length === 0}/>);
}
function renderEmailStep({ email, emailError, onEmailChange, onEmailProceed, onBackStep, }: CfooterContactFormProps): ReactNode {
    return (<Ccontactformstep placeholder={emailError ?? 'Your email. Preferably real.'} value={email} onChange={onEmailChange} buttonText={CONTACT_PROCEED_BUTTON_LABEL} onProceed={onEmailProceed} onBack={onBackStep} isError={!!emailError} isDisabled={!isValidEmail({ email: email.trim() })}/>);
}
function renderMessageStep({ message, messageError, isSubmitting, onMessageChange, onSubmit, onBackStep, }: CfooterContactFormProps): ReactNode {
    return (<Ccontactformstep placeholder={messageError ?? 'State Your Purpose'} value={message} onChange={onMessageChange} buttonText={CONTACT_SUBMIT_BUTTON_LABEL} onProceed={onSubmit} onBack={onBackStep} rows={4} isLoading={isSubmitting} isError={!!messageError} isDisabled={message.trim().length === 0}/>);
}
function renderDoneStep(): ReactNode {
    return (<Ccontainer display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      <Chackerheader text="The CABAL will contact you" temper={Temper.Good}/>
    </Ccontainer>);
}
function renderErrorStep({ onErrorBackToName }: CfooterContactFormProps): ReactNode {
    return (<Ccontainer display="flex" flexDirection="column" alignItems="center" justifyContent="center" contentWrapperStyle={{ gap: '20px' }}>
      <Chackerheader text="You did something wrong" temper={Temper.Bad}/>
      <Cbutton preset={ButtonPreset.Secondary} icon={IconChevronLeft} iconPosition={BUTTON_ICON_POSITION.Left} onClick={onErrorBackToName}/>
    </Ccontainer>);
}
export function CfooterContactForm(props: CfooterContactFormProps): ReactElement {
    return (
      <Ccontainer display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            padding: '0 24px',
        }}>
        <div data-el="Cfooter/ContactForm/Panel" style={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
          <Canimatedswitch dataElScope="Cfooter/ContactForm" contentKey={props.contactStep} translateAmount={30} duration={300} flex1={false} style={{ alignItems: 'center' }}>
            {CONTACT_STEP_RENDERERS[props.contactStep](props)}
          </Canimatedswitch>
        </div>
      </Ccontainer>
    );
}
